import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTeleprompter } from "@/hooks/useTeleprompter";
import { useScripts, ScriptSettings } from "@/hooks/useScripts";
import { useVoiceSync } from "@/hooks/useVoiceSync";
import { useSubscription } from "@/hooks/useSubscription";
import { useWakeLock } from "@/hooks/useWakeLock";
import { useTeleprompterPersistence } from "@/hooks/useTeleprompterPersistence";
import { TeleprompterDisplay } from "@/components/teleprompter/TeleprompterDisplay";
import { TopControls } from "@/components/teleprompter/TopControls";
import { UnifiedControlBar } from "@/components/recording/UnifiedControlBar";
import { Watermark } from "@/components/teleprompter/Watermark";
import { ProgressBar } from "@/components/teleprompter/ProgressBar";
import { useScriptLimits } from "@/hooks/useScriptLimits";
import { useMediaRecorder } from "@/hooks/useMediaRecorder";
import { useMicrophoneDevices } from "@/hooks/useMicrophoneDevices";
import { useFFmpeg } from "@/hooks/useFFmpeg";
import { useAudioLevel } from "@/hooks/useAudioLevel";
import { useToast } from "@/hooks/use-toast";
import { AudioFormat, AudioQuality } from "@/types/recording";
import { mimeTypes, fileExtensions, mergeAudio } from "@/lib/ffmpeg";
import { AudioWaveform } from "@/components/recording/AudioWaveform";
import { PlaybackOptions as PlaybackOptionsType } from "@/types/recording";
import { RecordingData } from "@/hooks/useAudioPersistence";
import { CountdownOverlay } from "@/components/teleprompter/CountdownOverlay";
import { RecordingPreviewDialog } from "@/components/recording/RecordingPreviewDialog";
import { DownloadProgressModal, DownloadStep } from "@/components/recording/DownloadProgressModal";

const defaultSettings: ScriptSettings = {
  fontSize: 48,
  fontColor: "#ffffff",
  bgColor: "#1a1a1a",
  scrollSpeed: 50,
  mirrorMode: false,
};

export default function Teleprompter() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getScript } = useScripts();
  const { canUseMirrorMode } = useScriptLimits();
  const { isPro } = useSubscription();
  
  const [script, setScript] = useState<{
    content: string;
    scene_type: string;
    settings: ScriptSettings;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [voiceSyncEnabled, setVoiceSyncEnabled] = useState(false);
  const [lineHeight, setLineHeight] = useState(1.5);
  const [horizontalPadding, setHorizontalPadding] = useState(10);
  const [menuOpacity, setMenuOpacity] = useState(0.8);

  // Wake Lock - keep screen on
  const { requestWakeLock, releaseWakeLock } = useWakeLock();

  // Persistence - save/restore state
  const { saveState, loadState, clearState } = useTeleprompterPersistence(id);
  const hasRestoredRef = useRef(false);
  const wakeLockRequestedRef = useRef(false);

  // Microphone Devices
  const { 
    devices, 
    selectedDeviceId, 
    selectedDeviceLabel, 
    selectDevice 
  } = useMicrophoneDevices();

  // Output - Recording
  const {
    state: recordingState,
    error: recordingError,
    permissionStatus,
    startRecording,
    prepareStream,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    checkPermission,
    stream,
  } = useMediaRecorder(selectedDeviceId || undefined);

  // FFmpeg for MP3 conversion
  const { convert: convertToMp3, status: ffmpegStatus } = useFFmpeg();

  // Audio Format & Quality
  const [audioFormat, setAudioFormat] = useState<AudioFormat>('webm');
  const [audioQuality, setAudioQuality] = useState<AudioQuality>('standard');
  const [isConverting, setIsConverting] = useState(false);
  
  // Download Progress Modal
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadStep, setDownloadStep] = useState<DownloadStep>('idle');
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadError, setDownloadError] = useState<string | undefined>();
  const [preparedBlob, setPreparedBlob] = useState<Blob | null>(null);
  const [downloadFileName, setDownloadFileName] = useState<string>('');

  // Toast for error messages
  const { toast } = useToast();

  // 格式/質量聯動處理
  const handleFormatChange = useCallback((format: AudioFormat) => {
    setAudioFormat(format);
    // 當選擇 WAV 時，自動設為無損
    if (format === 'wav') {
      setAudioQuality('lossless');
    }
    // 當從 WAV 切換到其他格式且當前是無損時，切換到高質量
    if (format !== 'wav' && audioQuality === 'lossless') {
      setAudioQuality('high');
    }
  }, [audioQuality]);

  const handleQualityChange = useCallback((quality: AudioQuality) => {
    // 當選擇無損且格式不是 WAV 時，自動切換到 WAV
    if (quality === 'lossless' && audioFormat !== 'wav') {
      setAudioFormat('wav');
    }
    setAudioQuality(quality);
  }, [audioFormat]);

  // 音頻預覽 Stream（頁面載入時獲取，用於顯示音量指示器）
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);

  // Audio Level Logic - 優先使用錄音 stream，否則使用預覽 stream
  const audioLevel = useAudioLevel(stream || previewStream);

  // Playback Options
  const [playbackOptions, setPlaybackOptions] = useState<PlaybackOptionsType>({
    teleprompter: true,
    recording: true,
  });

  // Preview
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Recording constraints
  const maxRecordingDuration = isPro ? undefined : 600; // 10 minutes for non-Pro

  // Auto-stop when reaching limit
  useEffect(() => {
    if (maxRecordingDuration && recordingState.duration >= maxRecordingDuration) {
      stopRecording();
    }
  }, [recordingState.duration, maxRecordingDuration, stopRecording]);

  // 獲取預覽用的麥克風 Stream（頁面載入時持續開啟，用於顯示音量指示器）
  useEffect(() => {
    let previewStreamRef: MediaStream | null = null;
    
    const startPreview = async () => {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          const audioConstraints: MediaTrackConstraints = {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          };
          
          // 如果有選擇特定設備，使用該設備
          if (selectedDeviceId) {
            audioConstraints.deviceId = { exact: selectedDeviceId };
          }
          
          previewStreamRef = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
          setPreviewStream(previewStreamRef);
        }
      } catch (err) {
        console.log('[Preview] Could not get microphone for preview:', err);
      }
    };
    
    startPreview();
    
    // 清理：離開頁面時停止預覽 stream
    return () => {
      if (previewStreamRef) {
        previewStreamRef.getTracks().forEach(track => track.stop());
        setPreviewStream(null);
      }
    };
  }, [selectedDeviceId]);

  // Load script
  useEffect(() => {
    const loadScript = async () => {
      if (!id) {
        navigate("/dashboard");
        return;
      }

      const data = await getScript(id);
      if (data) {
        setScript({
          content: data.content,
          scene_type: data.scene_type,
          settings: data.settings,
        });
        if (data.settings.lineHeight) setLineHeight(data.settings.lineHeight);
        if (data.settings.horizontalPadding) setHorizontalPadding(data.settings.horizontalPadding);
      } else {
        navigate("/dashboard");
      }
      setLoading(false);
    };

    loadScript();
  }, [id, getScript, navigate]);

  const {
    state,
    containerRef,
    preset,
    estimatedTotalTime,
    remainingTime,
    scrollProgress,
    isAtEnd,
    scrollPosition,
    togglePlay,
    setSpeed,
    adjustFontSize,
    setFontSize,
    toggleMirrorMode,
    jumpTime,
    reset,
    manualScroll,
    showControlsTemporarily,
    hideControls,
    syncScrollPosition,
    toggleControlsPinned,
    setScrollPosition,
    setElapsedTime,
  } = useTeleprompter(
    script?.content || "",
    script?.scene_type || "recording",
    script?.settings || defaultSettings
  );

  // Release wake lock when component unmounts
  useEffect(() => {
    return () => {
      releaseWakeLock();
    };
  }, [releaseWakeLock]);

  // Theme state
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  // Restore state from persistence on mount
  useEffect(() => {
    if (!loading && script && !hasRestoredRef.current) {
      const savedState = loadState();
      if (savedState) {
        // Restore all saved values
        if (savedState.scrollPosition !== undefined && setScrollPosition) {
          setScrollPosition(savedState.scrollPosition);
        }
        if (savedState.elapsedTime !== undefined && setElapsedTime) {
          setElapsedTime(savedState.elapsedTime);
        }
        if (savedState.speed !== undefined) {
          setSpeed(savedState.speed);
        }
        if (savedState.fontSize !== undefined && setFontSize) {
          setFontSize(savedState.fontSize);
        }
        if (savedState.lineHeight !== undefined) {
          setLineHeight(savedState.lineHeight);
        }
        if (savedState.horizontalPadding !== undefined) {
          setHorizontalPadding(savedState.horizontalPadding);
        }
        if (savedState.theme !== undefined) {
          setTheme(savedState.theme);
        }
        if (savedState.voiceSyncEnabled !== undefined) {
          setVoiceSyncEnabled(savedState.voiceSyncEnabled);
        }
      }
      hasRestoredRef.current = true;
    }
  }, [loading, script, loadState, setSpeed, setFontSize, setScrollPosition, setElapsedTime]);

  // Auto-save state periodically and on visibility change
  useEffect(() => {
    if (!script || loading) return;

    const saveCurrentState = () => {
      saveState({
        scrollPosition: state.scrollPosition,
        elapsedTime: state.elapsedTime,
        isPlaying: state.isPlaying,
        speed: state.speed,
        fontSize: state.fontSize,
        mirrorMode: state.mirrorMode,
        lineHeight,
        horizontalPadding,
        theme,
        voiceSyncEnabled,
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveCurrentState();
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      saveCurrentState();
      
      // Warn if recording
      if (recordingState.status === 'recording') {
        e.preventDefault();
        e.returnValue = ''; // Standard for Chrome
        return '';
      }
    };

    const saveInterval = setInterval(saveCurrentState, 5000);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(saveInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      saveCurrentState();
    };
  }, [script, loading, state, lineHeight, horizontalPadding, theme, voiceSyncEnabled, saveState, recordingState.status]);

  // Calculate line height in pixels for voice sync
  const voiceSyncLineHeight = state.fontSize * lineHeight;

  // Voice sync scroll handler
  const handleVoiceSyncScroll = useCallback((position: number) => {
    if (containerRef.current) {
      containerRef.current.scrollTop = position;
    }
  }, [containerRef]);

  const voiceSync = useVoiceSync({
    content: script?.content || "",
    lineHeight,
    onScrollTo: handleVoiceSyncScroll,
  });

  // Toggle voice sync
  const toggleVoiceSync = useCallback(() => {
    if (voiceSyncEnabled) {
      voiceSync.stopListening();
      setVoiceSyncEnabled(false);
    } else {
      voiceSync.startListening();
      setVoiceSyncEnabled(true);
    }
  }, [voiceSyncEnabled, voiceSync]);

  // Handle padding change
  const handlePaddingChange = useCallback((newPadding: number) => {
    const oldWidth = 100 - 2 * horizontalPadding;
    const newWidth = 100 - 2 * newPadding;
    
    if (newWidth > 0 && oldWidth > 0) {
       const ratio = oldWidth / newWidth;
       const newSpeed = state.speed * ratio;
       setSpeed(Math.round(newSpeed));
    }
    setHorizontalPadding(newPadding);
  }, [horizontalPadding, state.speed, setSpeed]);


  
  // -- SYNCED LOGIC START --

  // Recording state (no longer using IndexedDB persistence)
  const [isCountdownVisible, setIsCountdownVisible] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // State for merging
  const [lastRecording, setLastRecording] = useState<RecordingData | null>(null);

  // Mobile Menu State (Lifted from UnifiedControlBar)
  const [mobileTab, setMobileTab] = useState<'teleprompter' | 'audio'>('teleprompter');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Navigate to Audio Tab Handler
  const handleGoToAudio = useCallback(() => {

    setShowPreview(false);
    showControlsTemporarily(); // Ensure controls are visible (so drawer isn't auto-closed)
    setMobileTab('audio');
    setIsMobileMenuOpen(true);
  }, [showControlsTemporarily]);

  // Handle Recording Stop - store in session state only (no IndexedDB)
  useEffect(() => {
    if (recordingState.status === 'stopped') {
      const processRecording = async () => {
        let finalBlob = recordingState.audioBlob;

        // ALWAYS process through mergeAudio to ensure mono -> balanced conversion and volume gain
        // This fixes the issue where single segments play in mono (left side) in preview
        if (recordingState.audioSegments && recordingState.audioSegments.length >= 1) {
            setIsProcessing(true);
            try {
                console.log(`[Teleprompter] Processing ${recordingState.audioSegments.length} segment(s) for preview...`);
                finalBlob = await mergeAudio(recordingState.audioSegments);
            } catch (err) {
                console.error('Failed to process audio for preview:', err);
                // Fallback to raw first segment if merge fails
                finalBlob = recordingState.audioSegments[0];
            } finally {
                setIsProcessing(false);
            }
        }

        if (finalBlob) {
            const id = crypto.randomUUID();
            const newRecording = {
              id,
              blob: finalBlob,
              duration: recordingState.duration,
              timestamp: Date.now(),
              scriptId: id,
              name: `Take ${new Date().toLocaleTimeString()}`
            };

            setLastRecording(newRecording);
            setShowPreview(true);
        }
      };
      
      processRecording();
    }
  }, [recordingState.status, recordingState.audioBlob, recordingState.duration, recordingState.audioSegments]);

  // Handle Discard - Clear both hook state and screen session state
  const handleDiscardRecording = useCallback(() => {
    if (confirm(t('teleprompter.recording.discardConfirm'))) {
      resetRecording();
      setLastRecording(null);
      reset(); // Also reset teleprompter to top
      // If we are in Audio tab, we might want to switch back or stay there?
      // Stay there is fine, state will just be empty.
    }
  }, [resetRecording, t, reset]);

  const handleCountdownComplete = useCallback(async () => {
    setIsCountdownVisible(false);
    try {
      // startRecording 現在是瞬發的，因為 prepareStream 已經在倒數期間完成預熱
      await startRecording(selectedDeviceId);
      if (!state.isPlaying) {
        togglePlay(); // Start Scroll
      }
    } catch (e) {
      console.error("Failed to start recording after countdown", e);
    }
  }, [startRecording, selectedDeviceId, state.isPlaying, togglePlay]);

  // Synced Play Handler
  const handleSyncedPlay = useCallback(async () => {
    // 1. If currently playing (Rec + Scroll) -> Pause Both
    if (state.isPlaying) {
      togglePlay(); // Pause Scroll
      // Force call pauseRecording regardless of React state to fix race condition
      pauseRecording();
      return;
    }

    // 2. If currently Paused (Scroll Paused) -> Resume Both  
    if (!state.isPlaying) {
       if (recordingState.status === 'paused' || recordingState.status === 'recording') {
          // If was paused (or stuck in inconsistent state), resume
          togglePlay(); // Resume Scroll
          // Force call resumeRecording regardless of React state to fix race condition
          resumeRecording();
          return;
       }
    }

    // 3. If Idle/Stopped -> Verify Mic & Start Countdown
    if (!state.isPlaying && (recordingState.status === 'idle' || recordingState.status === 'stopped')) {
        await requestWakeLock();
        
        // 開始倒數前，先觸發麥克風預熱 (300ms)
        console.log('[Teleprompter] Pre-warming microphone...');
        prepareStream(selectedDeviceId); 
        
        setIsCountdownVisible(true);
        hideControls(); // <--- Added: Hide controls immediately when countdown starts
    }
  }, [state.isPlaying, recordingState.status, togglePlay, pauseRecording, resumeRecording, requestWakeLock, hideControls, prepareStream, selectedDeviceId]); // Added hideControls to dependency array

  // Synced Stop Handler
  const handleSyncedStop = useCallback(() => {
      if (recordingState.status !== 'idle' && recordingState.status !== 'stopped') {
          stopRecording(); // This will trigger the useEffect above to save and show preview
      }
      reset(); // Reset scroll to top
  }, [recordingState.status, stopRecording, reset]);


  // -- SYNCED LOGIC END --

  // Update handleKeyDown dependencies to include new handlers
  useEffect(() => {

    const handleKey = (e: KeyboardEvent) => {
        // 當焦點在輸入框時，忽略快捷鍵（避免輸入文字時意外觸發）
        const target = e.target as HTMLElement;
        if (
          target.tagName === 'INPUT' || 
          target.tagName === 'TEXTAREA' || 
          target.isContentEditable
        ) {
          return;
        }
        
        if (e.code === "Space") {
            e.preventDefault();
            handleSyncedPlay();
        } else if (e.code === "ArrowUp") {
            e.preventDefault();
            setSpeed(state.speed + 10);
        } else if (e.code === "ArrowDown") {
            e.preventDefault();
            setSpeed(state.speed - 10);
        } else if (e.code === "ArrowLeft") {
            e.preventDefault();
            jumpTime(-5);
        } else if (e.code === "ArrowRight") {
            e.preventDefault();
            jumpTime(5);
        } else if (e.code === "Escape") {
            e.preventDefault();
            navigate(`/editor/${id}`);
        }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleSyncedPlay, state.speed, setSpeed, jumpTime, navigate, id]);

  const handleTouchStart = (e: React.TouchEvent) => {
    // Enable wake lock on first user touch (required for iOS Safari)
    if (!wakeLockRequestedRef.current) {
      requestWakeLock();
      wakeLockRequestedRef.current = true;
    }
  };

  const handleExit = () => {
    // Release wake lock
    releaseWakeLock();
    // Clear saved state when user explicitly exits
    clearState();
    navigate(`/editor/${id}`);
  };

  const handleMouseMove = useCallback(() => {
    // Ignore mouse events on touch devices
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    showControlsTemporarily();
  }, [showControlsTemporarily]);
  
  const handleDisplayClick = useCallback(() => {
    // Tap on display area → Toggle Play/Pause (Synced)

    handleSyncedPlay();
    
    // Also ensure controls are shown when pausing
    if (state.isPlaying) { // If currently playing (and about to pause)
        showControlsTemporarily();
    }
  }, [handleSyncedPlay, state.isPlaying, showControlsTemporarily]);

  // 處理下載流程
  const startDownloadProcess = useCallback(async (blob: Blob, format: AudioFormat, quality: AudioQuality) => {
    setDownloadStep('converting');
    setDownloadProgress(0);
    setDownloadError(undefined);
    setPreparedBlob(null);

    // 為了確保 Blob 的穩定性（避免從 IDB 或 Stream 讀取時的引用問題），
    // 我們執行一次 Deep Clone (ArrayBuffer -> New Blob)
    const stableBlob = await (async () => {
      try {

        const buffer = await blob.arrayBuffer();
        return new Blob([buffer], { type: blob.type });
      } catch (e) {
        console.error('Input blob cloning failed, using original:', e);
        return blob;
      }
    })();

    // 如果是 WebM 或原始格式，不需要轉換
    if (format === 'webm') {
      setPreparedBlob(stableBlob);
      setDownloadStep('ready');
      setDownloadProgress(100);
      return;
    }

    try {
      // 開始轉換
      setIsConverting(true);
      const converted = await convertToMp3(stableBlob, {
        format,
        quality,
      }, (progress) => {
        setDownloadProgress(progress);
      });

      if (converted) {
        setPreparedBlob(converted);
        setDownloadStep('ready');
        setDownloadProgress(100);
      } else {
        throw new Error(t('teleprompter.recording.convertReturnsEmpty'));
      }
    } catch (err) {
      console.error('Download process error:', err);
      setDownloadError(err instanceof Error ? err.message : t('teleprompter.recording.convertFailed'));
      setDownloadStep('error');
      // 即使失敗，也準備原始 blob 作為備份
      setPreparedBlob(blob);
    } finally {
      setIsConverting(false);
    }
  }, [convertToMp3, t]);

  const handleDownload = useCallback(async () => {
    const audioBlob = recordingState.audioBlob || lastRecording?.blob;
    const duration = recordingState.duration || lastRecording?.duration || 0;
    
    if (!audioBlob || audioBlob.size === 0) {
      toast({
        title: t('teleprompter.recording.cannotDownload'),
        description: t('teleprompter.recording.noRecordingData'),
        variant: 'destructive',
      });
      return;
    }

    // WAV 大文件警告
    if (audioFormat === 'wav' && audioQuality === 'lossless' && duration > 300) {
      const estimatedSizeMB = Math.round((duration / 60) * 10);
      const confirmed = window.confirm(
        t('teleprompter.recording.wavLargeWarning', { minutes: Math.round(duration / 60), size: estimatedSizeMB })
      );
      if (!confirmed) return;
    }

    const filename = `recording_${Date.now()}`;
    setDownloadFileName(filename);
    setShowDownloadModal(true);
    
    // 開始處理
    startDownloadProcess(audioBlob, audioFormat, audioQuality);
  }, [recordingState.audioBlob, recordingState.duration, lastRecording, audioFormat, audioQuality, toast, startDownloadProcess, t]);

  // 執行最終下載（瀏覽器行為）
  const executeBrowserDownload = useCallback(() => {
    if (!preparedBlob || !downloadFileName) {
      console.error('Execute download failed: missing blob or filename', { preparedBlob, downloadFileName });
      return;
    }

    console.log('Executing browser download:', {
      size: preparedBlob.size,
      type: preparedBlob.type,
      filename: downloadFileName
    });

    try {
      // 回退到標準 Blob URL 下載，但移除了手動 revokeObjectURL
      // 讓瀏覽器自己在頁面刷新/關閉時清理，徹底避免 ERR_FILE_NOT_FOUND
      const url = URL.createObjectURL(preparedBlob);
      
      const a = document.createElement('a');
      a.href = url;
      
      // 確定擴展名
      let ext = fileExtensions[audioFormat];
      if (downloadStep === 'error' || (audioFormat !== 'webm' && preparedBlob.type.includes('webm'))) {
        ext = fileExtensions['webm'];
      }

      a.download = `${downloadFileName}${ext}`;
      
      // 必須添加到 body 才能在 Firefox 等瀏覽器正常工作
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      

      
      // 不執行 revokeObjectURL(url)，依賴頁面生命週期結束自動回收
      // 這樣可以 100% 避免 "點擊下載時文件已消失" 的問題
      
      // 下載後關閉模態框
      setShowDownloadModal(false);
      
    } catch (e) {
      console.error('Download failed:', e);
      toast({
        title: t('teleprompter.recording.downloadFailed'),
        description: t('teleprompter.recording.downloadFailedDesc'),
        variant: 'destructive',
      });
    }
  }, [preparedBlob, downloadFileName, audioFormat, downloadStep, toast, t]);

  if (loading) {
    return (
      <div className="h-screen w-screen bg-foreground flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!script) {
    return null;
  }
  
  const displayBgColor = theme === 'light' ? '#ffffff' : (script.settings.bgColor || '#1a1a1a');
  const displayFontColor = theme === 'light' ? '#000000' : (script.settings.fontColor || '#ffffff');

  return (
    <div 
      className={`relative w-full h-screen overflow-hidden select-none touch-none ${!state.showControls ? 'cursor-none' : ''} ${theme === 'dark' ? 'dark' : ''}`}
      style={{
        backgroundColor: displayBgColor,
        cursor: state.showControls ? 'default' : 'none' 
      }}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
    >
      <audio ref={audioRef} className="hidden" />
      <ProgressBar progress={scrollProgress} />

      <CountdownOverlay 
        isVisible={isCountdownVisible} 
        onComplete={handleCountdownComplete} 
      />
      
      {/* Processing Indicator */}
      {isProcessing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-zinc-800 p-6 rounded-2xl shadow-xl flex flex-col items-center gap-4 border-2 border-black">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-zinc-200 border-t-yellow-400"></div>
            <p className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{t('teleprompter.recording.processing')}</p>
            <p className="text-sm text-zinc-500">{t('teleprompter.recording.merging')}</p>
          </div>
        </div>
      )}

      {/* Recording Preview Dialog */}
      <RecordingPreviewDialog 
        isOpen={showPreview && !isProcessing}
        onClose={() => setShowPreview(false)}
        recording={lastRecording}
        onDelete={() => {
          setLastRecording(null);
          resetRecording();
        }}
        onGoToAudio={handleGoToAudio}
      />

      <DownloadProgressModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        step={downloadStep}
        progress={downloadProgress}
        format={audioFormat}
        fileName={downloadFileName}
        errorMessage={downloadError}
        onDownload={executeBrowserDownload}
        onCancel={() => {
          setShowDownloadModal(false);
          setIsConverting(false);
        }}
      />

      <TopControls
        elapsedTime={state.elapsedTime}
        totalTime={estimatedTotalTime}
        showTimer={preset.showTimer}
        theme={theme}
        onToggleTheme={toggleTheme}
        mirrorMode={state.mirrorMode}
        onToggleMirror={toggleMirrorMode}
        onExit={handleExit}
        isPinned={state.isControlsPinned}
        onTogglePin={toggleControlsPinned}
        onReset={reset}
        stream={stream || previewStream}
        isRecording={recordingState.status === 'recording'}
      />



      <TeleprompterDisplay
        ref={containerRef}
        content={script.content}
        fontSize={state.fontSize}
        fontColor={displayFontColor}
        bgColor={displayBgColor}
        mirrorMode={state.mirrorMode}
        scrollPosition={scrollPosition}
        lineHeight={lineHeight}
        horizontalPadding={horizontalPadding}
        onMouseMove={handleMouseMove} 
        onClick={handleDisplayClick}
        onScrollUpdate={syncScrollPosition}
        onManualScrollDelta={manualScroll}
        isPlaying={state.isPlaying}
      />

      {/* Error Display */}
      {recordingError && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-red-500 text-white text-sm rounded-lg shadow-lg flex items-center gap-2">
            <span>⚠️ {recordingError}</span>
            {permissionStatus === 'denied' && (
            <span className="underline cursor-pointer font-bold ml-1" onClick={() => window.location.reload()}>
                {t('teleprompter.recording.refreshPage')}
            </span>
            )}
        </div>
      )}

      {/* Unified Control Bar */}
      <UnifiedControlBar
        // Teleprompter Controls
        highlightDuration={(() => {
          const pixelSpeedPerSec = (preset.scrollSpeed / 1000) * 1000 * (state.speed / 100);
          return pixelSpeedPerSec > 0 ? (state.fontSize * lineHeight) / pixelSpeedPerSec : 2.5;
        })()}
        fontSize={state.fontSize}
        lineHeight={lineHeight}
        horizontalPadding={horizontalPadding}
        onHighlightDurationChange={(duration) => {
          if (duration <= 0 || preset.scrollSpeed <= 0) return;
          const pxPerSecNeeded = (state.fontSize * lineHeight) / duration;
          const speedPercent = (pxPerSecNeeded / (preset.scrollSpeed)) * 100;
          setSpeed(Math.round(speedPercent));
        }}
        onFontSizeChange={adjustFontSize}
        onLineHeightChange={setLineHeight}
        onHorizontalPaddingChange={setHorizontalPadding}
        menuOpacity={menuOpacity}
        onMenuOpacityChange={setMenuOpacity}
        
        // Playback Sync Logic
        isPlaying={state.isPlaying} 
        onTogglePlay={handleSyncedPlay} 
        
        // Recording Controls
        recordingState={recordingState}
        stream={stream}
        onStartRecording={() => { /* Handled by Synced Play */ }}
        onStopRecording={handleSyncedStop}
        
        // Device selection
        devices={devices}
        selectedDeviceId={selectedDeviceId}
        selectedDeviceLabel={selectedDeviceLabel}
        onMicrophoneChange={selectDevice}
        
        // Format & Download
        audioFormat={audioFormat}
        audioQuality={audioQuality}
        onFormatChange={handleFormatChange}
        onQualityChange={handleQualityChange}
        onDownloadAudio={handleDownload}
        onPreviewRecording={() => setShowPreview(true)}
        onDiscardRecording={handleDiscardRecording}
        hasRecording={!!recordingState.audioBlob || !!lastRecording}
        audioBlob={recordingState.audioBlob || lastRecording?.blob}
        recordingDuration={recordingState.duration || lastRecording?.duration || 0}
        isConverting={isConverting || ffmpegStatus === 'converting'}
        isExpanded={state.showControls} 
        isPinned={state.isControlsPinned}
        onTogglePin={toggleControlsPinned}
        
        // Lifted Mobile State
        activeMobileTab={mobileTab}
        onActiveMobileTabChange={setMobileTab}
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuOpenChange={setIsMobileMenuOpen}
      />

      <Watermark />
    </div>
  );
}
